/**
 * ProShip Direct v2 — ProShip Direct Performance.
 *
 * The one page that answers "was bringing my own contracts worth it". It sits under Performance &
 * Analytics rather than on the Direct home page because a merchant who has just activated has no
 * shipments of their own, so every figure would read zero on the landing page.
 *
 * THE ORGANISING IDEA: an aggregate comparison is nearly useless here. A merchant's own contract is
 * rarely better or worse outright — it is better in the metros it was negotiated for and worse in
 * the north-east, better under 500g and worse above two kilos, better on prepaid and worse on COD.
 * So the page leads with the totals and then cuts them five ways, because the cut is where the
 * action is: "move COD above 2kg back to ProShip" is a decision, "you are 1.8 points behind" is not.
 *
 * THE FALLBACK CARD is load-bearing for this module's whole model. Because every configuration
 * falls back to a ProShip default, a merchant can quote a lane their contract does not cover and
 * have it quietly ship on a ProShip courier. Nothing looks broken — the parcel is delivered — but
 * the lane they meant to migrate stayed with us. This is how they see that happening, and it is what
 * makes inheritance self-correcting.
 *
 * SAVINGS ARE GATED ON RATE CARDS, deliberately. ProShip does not hold the merchant's rates, so an
 * account with no uploaded card contributes volume and service figures but no cost — and the page
 * says how much of the volume the savings figure actually covers rather than presenting a partial
 * number as a total.
 *
 * All figures are illustrative — see the header on data/performanceData.js for exactly which
 * backend gap makes that unavoidable.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow, Alert, Chip, Button,
  Tooltip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { differenceInCalendarDays } from 'date-fns';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import PageHero from '../../../common/PageHero';
import { FormProvider, RHFDateRangePickerField } from '../../../common/hook-form';
import { BarChart } from '../../charts/ColumnChart';
import ComparisonBars from '../components/ComparisonBars';
import { pageContainerSx, sectionCardSx, sectionTitleSx, secondaryBtnSx } from '../../../styles/patterns';
import { statCardSx, statLabelSx, statValueSx, statSubSx, tHeadSx, tCellSx, tNumSx } from '../components/stats';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, PURPLE, ORANGE, SUCCESS, WARNING, ERROR } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { usePlan, useOwnCouriers } from '../data/hooks';
import { PlanInactive, NoAccounts } from '../components/states';
import { RANGE_PRESETS, buildPerformance, formatMetric, gapOf } from '../data/performanceData';
import { MONO } from '../components/directCellStyles';

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.6 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const int = (n) => Number(n || 0).toLocaleString('en-IN');

/** Own and ProShip get the same two colours everywhere on the page, chart and table alike. */
const OWN_COLOR = ORANGE;
const PROSHIP_COLOR = PURPLE;

/** A signed difference, coloured by whether it favours the merchant. */
function Gap({ value, ownWins, suffix = '', flat = false }) {
  if (value === null || value === undefined) return <Typography sx={smallSx}>—</Typography>;
  const color = flat ? SUBTLE : ownWins ? SUCCESS : ERROR;
  const sign = value > 0 ? '+' : '';
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color }}>
      {!flat &&
        (ownWins ? (
          <ArrowUpwardIcon sx={{ fontSize: 12, transform: value < 0 ? 'rotate(180deg)' : 'none' }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 12, transform: value > 0 ? 'rotate(180deg)' : 'none' }} />
        ))}
      <Typography sx={{ fontFamily: MONO, fontSize: SIZE['3xs'], fontWeight: WEIGHT.semibold, color }}>
        {sign}
        {value}
        {suffix}
      </Typography>
    </Box>
  );
}

/**
 * A titled comparison block, so the cuts read as one family.
 *
 * `fill` makes the card take the full height of its grid row, for the two that sit side by side —
 * without it each sizes to its own content and the row ends with a ragged edge.
 */
function Cut({ title, blurb, children, action, fill = false }) {
  return (
    <Box sx={{ ...sectionCardSx, ...(fill ? { height: '100%', width: '100%' } : null) }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: GAP.sm, mb: 1.25, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ ...sectionTitleSx, mb: 0 }}>{title}</Typography>
          {blurb && <Typography sx={smallSx}>{blurb}</Typography>}
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
}

/**
 * A header for a table whose columns come in Direct/ProShip pairs.
 *
 * Written as two rows — a spanning group name over a Direct and a ProShip column — because the
 * compact alternative these tables used, "87.7% vs 91.1%" in one cell, never says which number is
 * which. The reader has to infer it from the card's title and get it right every time.
 *
 * `solo` columns span both rows: they belong to neither side (Mode, Zone) or are Direct-only.
 */
function PairedHead({ solo, pairs, trailing = [] }) {
  return (
    <TableHead>
      <TableRow>
        {solo.map((c) => (
          <TableCell key={c.label} rowSpan={2} sx={{ ...tHeadSx, minWidth: c.minWidth, textAlign: c.align || 'left' }}>
            {c.label}
          </TableCell>
        ))}
        {pairs.map((c) => (
          <TableCell
            key={c.label}
            colSpan={2}
            align='center'
            sx={{ ...tHeadSx, borderLeft: `1px solid ${BORDER}` }}
          >
            {c.label}
          </TableCell>
        ))}
        {trailing.map((c) => (
          <TableCell key={c.label} rowSpan={2} sx={{ ...tHeadSx, minWidth: c.minWidth, textAlign: c.align || 'left' }}>
            {c.label}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        {pairs.flatMap((c) => [
          <TableCell
            key={`${c.label}-own`}
            sx={{ ...tHeadSx, minWidth: c.minWidth, textAlign: 'right', borderLeft: `1px solid ${BORDER}` }}
          >
            <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: OWN_COLOR }} />
              Direct
            </Box>
          </TableCell>,
          <TableCell key={`${c.label}-proship`} sx={{ ...tHeadSx, minWidth: c.minWidth, textAlign: 'right' }}>
            <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: PROSHIP_COLOR }} />
              ProShip
            </Box>
          </TableCell>,
        ])}
      </TableRow>
    </TableHead>
  );
}

/** The first cell of a pair carries the divider, so the grouping stays visible in the body. */
const pairCellSx = { ...tNumSx, borderLeft: `1px solid ${BORDER}` };

/** The two-colour key, repeated wherever a table has an Own and a ProShip column. */
function OwnerHead() {
  return (
    <>
      <TableCell sx={{ ...tHeadSx, minWidth: 90, textAlign: 'right' }}>
        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: OWN_COLOR }} />
          Direct
        </Box>
      </TableCell>
      <TableCell sx={{ ...tHeadSx, minWidth: 90, textAlign: 'right' }}>
        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: PROSHIP_COLOR }} />
          ProShip
        </Box>
      </TableCell>
    </>
  );
}

function DirectPerformance() {
  const { plan } = usePlan();
  const { couriers } = useOwnCouriers();
  const [preset, setPreset] = useState('30d');

  const methods = useForm({ defaultValues: { fromDate: '', toDate: '' } });
  const { watch, reset } = methods;
  const fromDate = watch('fromDate');
  const toDate = watch('toDate');

  /**
   * The window, in days.
   *
   * A custom range wins over the preset chips, because picking dates is the more deliberate act —
   * and selecting one clears the other so the two controls can never both look active.
   */
  const days = useMemo(() => {
    if (fromDate && toDate) {
      return Math.max(1, differenceInCalendarDays(new Date(toDate), new Date(fromDate)) + 1);
    }
    return RANGE_PRESETS.find((r) => r.key === preset)?.days || 30;
  }, [fromDate, toDate, preset]);

  const data = useMemo(() => buildPerformance({ days, couriers }), [days, couriers]);

  if (!plan.active) {
    return (
      <Box sx={pageContainerSx}>
        <PageTitle>ProShip Direct Performance</PageTitle>
        <PlanInactive what='Comparing your couriers against ProShip’s' />
      </Box>
    );
  }
  if (!couriers.length) {
    return (
      <Box sx={pageContainerSx}>
        <PageTitle>ProShip Direct Performance</PageTitle>
        <NoAccounts />
      </Box>
    );
  }

  const { metrics, zones, paymentModes, weightSlabs, byCourier, trend, topStates, fallback, savings } = data;
  const shareMetric = metrics.find((m) => m.key === 'share');
  const deliveryGap = gapOf(metrics.find((m) => m.key === 'delivery'));
  const fallbackTotal = fallback.reduce((sum, f) => sum + f.value, 0);
  const scored = metrics.filter((m) => m.better);
  const ownWins = scored.filter((m) => gapOf(m).ownWins).length;

  const byKey = (key) => metrics.find((m) => m.key === key);
  const rowFor = (key, label) => {
    const m = byKey(key);
    return { label: label || m.label, own: m.own, proship: m.proship };
  };

  /**
   * Two blocks, because the two run in opposite directions: more delivery is better, more RTO is
   * worse. Each declares its own direction so a green gap always means "good for you".
   */
  const serviceBlocks = [
    {
      title: 'Service',
      note: 'higher is better',
      betterIsHigh: true,
      scaleMax: 100,
      rows: [
        rowFor('delivery'),
        rowFor('firstAttempt', 'Delivered on first attempt'),
        rowFor('eddMet', 'Delivered by promised date'),
        rowFor('pickupSla', 'Picked up on time'),
      ],
    },
    {
      title: 'Failure',
      note: 'lower is better',
      betterIsHigh: false,
      rows: [
        { ...rowFor('rto', 'RTO %'), scaleMax: 12 },
        { ...rowFor('lost', 'Lost or damaged (per 10,000)'), scaleMax: 5 },
      ],
    },
  ];

  const chartOptions = (xaxis) => ({
    xaxis,
    data: [
      { dataKey: 'Direct', label: 'Direct couriers', fill: OWN_COLOR },
      { dataKey: 'ProShip', label: 'ProShip couriers', fill: PROSHIP_COLOR },
    ],
  });

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Your Direct couriers measured against ProShip’s, lane for lane'>
        ProShip Direct Performance
      </PageTitle>

      <PageHero
        title='Direct vs ProShip'
        subtitle={`Last ${days} days · ${int(data.own.shipments)} shipments on Direct couriers`}
      />

      {/* ── Window ── */}
      <Box sx={{ ...sectionCardSx, mb: 2 }}>
        <FormProvider methods={methods}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.md, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size='small'
              exclusive
              value={fromDate && toDate ? null : preset}
              onChange={(e, next) => {
                if (!next) return;
                setPreset(next);
                reset({ fromDate: '', toDate: '' });
              }}
            >
              {RANGE_PRESETS.map((r) => (
                <ToggleButton
                  key={r.key}
                  value={r.key}
                  sx={{ fontFamily: FONT, fontSize: SIZE['3xs'], textTransform: 'none', py: 0.5, px: 1.25 }}
                >
                  {r.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Box sx={{ minWidth: 210 }}>
              <RHFDateRangePickerField name='fromDate' label='Or pick dates' maxDate={new Date()} />
            </Box>

            {fromDate && toDate && (
              <Button size='small' sx={secondaryBtnSx} onClick={() => reset({ fromDate: '', toDate: '' })}>
                Clear dates
              </Button>
            )}

            <Box sx={{ flex: 1 }} />
            <Typography sx={smallSx}>Comparing {days} days of shipments</Typography>
          </Box>
        </FormProvider>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
        {/* ── Headline ── */}
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={3}>
            <Box sx={{ ...statCardSx, borderLeft: `3px solid ${OWN_COLOR}` }}>
              <Typography sx={statLabelSx}>On Direct couriers</Typography>
              <Typography sx={statValueSx}>{int(data.own.shipments)}</Typography>
              <Typography sx={statSubSx}>{shareMetric.own}% of everything you shipped</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={{ ...statCardSx, borderLeft: `3px solid ${savings.total > 0 ? SUCCESS : WARNING}` }}>
              <Typography sx={statLabelSx}>{savings.total >= 0 ? 'Saved this period' : 'Cost you extra'}</Typography>
              <Typography sx={{ ...statValueSx, color: savings.total >= 0 ? SUCCESS : ERROR }}>
                {savings.accountsWithCard ? inr(Math.abs(savings.total)) : '—'}
              </Typography>
              <Typography sx={statSubSx}>
                {savings.accountsWithCard
                  ? `${inr(Math.abs(savings.perShipment))} a shipment · ${savings.accountsWithCard} of ${savings.accountsTotal} accounts priced`
                  : 'no rate card uploaded, so cost cannot be compared'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={{ ...statCardSx, borderLeft: `3px solid ${deliveryGap.ownWins ? SUCCESS : WARNING}` }}>
              <Typography sx={statLabelSx}>Delivery gap</Typography>
              <Typography sx={{ ...statValueSx, color: deliveryGap.ownWins ? SUCCESS : WARNING }}>
                {deliveryGap.diff > 0 ? '+' : ''}
                {deliveryGap.diff}
              </Typography>
              <Typography sx={statSubSx}>
                points {deliveryGap.ownWins ? 'ahead of' : 'behind'} ProShip on delivered
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={{ ...statCardSx, borderLeft: `3px solid ${fallbackTotal > 0 ? WARNING : SUCCESS}` }}>
              <Typography sx={statLabelSx}>Fell back to ProShip</Typography>
              <Typography sx={{ ...statValueSx, color: fallbackTotal > 0 ? WARNING : NAVY }}>
                {int(fallbackTotal)}
              </Typography>
              <Typography sx={statSubSx}>orders you meant to ship Direct</Typography>
            </Box>
          </Grid>
        </Grid>

        {savings.accountsWithCard > 0 && savings.coverage < 100 && (
          <Alert severity='info' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
            The savings figure covers {savings.coverage}% of your Direct volume —{' '}
            {savings.accountsTotal - savings.accountsWithCard} account
            {savings.accountsTotal - savings.accountsWithCard === 1 ? ' has' : 's have'} no rate card,
            so ProShip cannot price what they carried. Upload their cards and this becomes a total.
          </Alert>
        )}

        {/* ── Everything, side by side ── */}
        <Grid container spacing={2} alignItems='stretch'>
          <Grid item xs={12} lg={5} sx={{ display: 'flex' }}>
            <Cut
              fill
              title='Service, side by side'
              blurb='The measures a buyer would notice. Both bars share one scale, so a small gap looks small.'
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.md, flexWrap: 'wrap' }}>
                  {[
                    ['Direct', OWN_COLOR],
                    ['ProShip', PROSHIP_COLOR],
                  ].map(([label, color]) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                      <Typography sx={smallSx}>{label}</Typography>
                    </Box>
                  ))}
                </Box>
              }
            >
              <ComparisonBars
                blocks={serviceBlocks}
                ownColor={OWN_COLOR}
                proshipColor={PROSHIP_COLOR}
                format={(v) => `${v}%`}
              />
            </Cut>
          </Grid>
          <Grid item xs={12} lg={7} sx={{ display: 'flex' }}>
            <Cut
              fill
              title='Every metric'
              blurb={`Direct couriers are ahead on ${ownWins} of ${scored.length} measures.`}
            >
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...tHeadSx, minWidth: 190 }}>Metric</TableCell>
                      <OwnerHead />
                      <TableCell sx={{ ...tHeadSx, minWidth: 80, textAlign: 'right' }}>Gap</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.map((m, i) => {
                      const gap = gapOf(m);
                      const newGroup = i === 0 || metrics[i - 1].group !== m.group;
                      return (
                        <React.Fragment key={m.key}>
                          {newGroup && (
                            <TableRow>
                              <TableCell
                                colSpan={4}
                                sx={{
                                  ...tCellSx,
                                  bgcolor: LIGHT_BG,
                                  fontWeight: WEIGHT.bold,
                                  fontSize: SIZE['4xs'],
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  color: SUBTLE,
                                  py: 0.5,
                                }}
                              >
                                {m.group}
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell sx={{ ...tCellSx, whiteSpace: 'normal' }}>{m.label}</TableCell>
                            <TableCell sx={tNumSx}>{formatMetric(m.fmt, m.own)}</TableCell>
                            <TableCell sx={tNumSx}>{formatMetric(m.fmt, m.proship)}</TableCell>
                            <TableCell sx={{ ...tNumSx }}>
                              {gap ? (
                                <Gap value={gap.diff} ownWins={gap.ownWins} flat={gap.isFlat} />
                              ) : (
                                <Typography sx={smallSx}>—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Cut>
          </Grid>
        </Grid>

        {/* ── By zone ── */}
        <Cut
          title='By zone'
          blurb='Where your Direct couriers were negotiated is usually where they win. The long tail is where they do not.'
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <BarChart
                data={zones.map((z) => ({ zone: z.zone, Direct: z.ownShipments, ProShip: z.proshipShipments }))}
                height={240}
                chartOptions={chartOptions('zone')}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <PairedHead
                    solo={[
                      { label: 'Zone', minWidth: 70 },
                      { label: 'Direct shipments', minWidth: 90, align: 'right' },
                    ]}
                    pairs={[
                      { label: 'Delivered', minWidth: 75 },
                      { label: 'Transit', minWidth: 70 },
                      { label: 'Cost / shipment', minWidth: 85 },
                    ]}
                    trailing={[{ label: 'Verdict', minWidth: 70, align: 'right' }]}
                  />
                  <TableBody>
                    {zones.map((z) => {
                      const costWin = z.ownCost < z.proshipCost;
                      const serviceWin = z.ownDelivery >= z.proshipDelivery;
                      return (
                        <TableRow key={z.zone}>
                          <TableCell sx={tCellSx}>{z.zone}</TableCell>
                          <TableCell sx={tNumSx}>{int(z.ownShipments)}</TableCell>
                          <TableCell sx={pairCellSx}>{z.ownDelivery}%</TableCell>
                          <TableCell sx={tNumSx}>{z.proshipDelivery}%</TableCell>
                          <TableCell sx={pairCellSx}>{z.ownTat}d</TableCell>
                          <TableCell sx={tNumSx}>{z.proshipTat}d</TableCell>
                          <TableCell sx={pairCellSx}>{inr(z.ownCost)}</TableCell>
                          <TableCell sx={tNumSx}>{inr(z.proshipCost)}</TableCell>
                          <TableCell sx={{ ...tNumSx }}>
                            <Chip
                              size='small'
                              label={costWin && serviceWin ? 'Keep' : costWin || serviceWin ? 'Mixed' : 'Review'}
                              sx={{
                                height: 18,
                                fontFamily: FONT,
                                fontSize: SIZE['4xs'],
                                fontWeight: WEIGHT.semibold,
                                bgcolor:
                                  costWin && serviceWin
                                    ? `${SUCCESS}14`
                                    : costWin || serviceWin
                                      ? `${WARNING}1f`
                                      : `${ERROR}14`,
                                color: costWin && serviceWin ? SUCCESS : costWin || serviceWin ? WARNING : ERROR,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Grid>
          </Grid>
        </Cut>

        {/* ── By payment mode and by weight ── */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Cut
              title='COD against prepaid'
              blurb='Almost all RTO lives in COD, so a single RTO number hides the only version that matters.'
            >
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <PairedHead
                    solo={[
                      { label: 'Mode', minWidth: 80 },
                      { label: 'Direct shipments', minWidth: 95, align: 'right' },
                    ]}
                    pairs={[
                      { label: 'Delivered', minWidth: 80 },
                      { label: 'Cost per shipment', minWidth: 90 },
                    ]}
                  />
                  <TableBody>
                    {paymentModes.map((p) => (
                      <TableRow key={p.mode}>
                        <TableCell sx={tCellSx}>{p.mode}</TableCell>
                        <TableCell sx={tNumSx}>{int(p.ownShipments)}</TableCell>
                        <TableCell sx={pairCellSx}>{p.ownDelivery}%</TableCell>
                        <TableCell sx={tNumSx}>{p.proshipDelivery}%</TableCell>
                        <TableCell sx={pairCellSx}>{inr(p.ownCost)}</TableCell>
                        <TableCell sx={tNumSx}>{inr(p.proshipCost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Cut>
          </Grid>

          <Grid item xs={12} md={6}>
            <Cut
              title='By weight slab'
              blurb='A negotiated rate is cut for the slab you ship most of, and rarely holds either side of it.'
            >
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...tHeadSx, minWidth: 105 }}>Slab</TableCell>
                      <TableCell sx={{ ...tHeadSx, minWidth: 80, textAlign: 'right' }}>Direct</TableCell>
                      <TableCell sx={{ ...tHeadSx, minWidth: 90, textAlign: 'right' }}>Your cost</TableCell>
                      <TableCell sx={{ ...tHeadSx, minWidth: 90, textAlign: 'right' }}>ProShip</TableCell>
                      <TableCell sx={{ ...tHeadSx, minWidth: 80, textAlign: 'right' }}>Gap</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {weightSlabs.map((w) => {
                      const diff = Number((w.ownCost - w.proshipCost).toFixed(2));
                      return (
                        <TableRow key={w.slab}>
                          <TableCell sx={tCellSx}>{w.slab}</TableCell>
                          <TableCell sx={tNumSx}>{int(w.ownShipments)}</TableCell>
                          <TableCell sx={tNumSx}>{inr(w.ownCost)}</TableCell>
                          <TableCell sx={tNumSx}>{inr(w.proshipCost)}</TableCell>
                          <TableCell sx={tNumSx}>
                            <Gap value={diff} ownWins={diff < 0} suffix='' />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              <Typography sx={{ ...smallSx, mt: 1 }}>
                Where the gap turns red, ProShip is cheaper for that slab — a candidate for a rule
                that sends it back.
              </Typography>
            </Cut>
          </Grid>
        </Grid>

        {/* ── By account ── */}
        <Cut
          title='Account by account'
          blurb='Which of your Direct couriers is actually earning its place.'
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tHeadSx, minWidth: 190 }}>Account</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 85, textAlign: 'right' }}>Shipments</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 85, textAlign: 'right' }}>Delivered</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 70, textAlign: 'right' }}>RTO</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 85, textAlign: 'right' }}>In transit</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 95, textAlign: 'right' }}>First attempt</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 105, textAlign: 'right' }}>Transit days (P2A)</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 95, textAlign: 'right' }}>Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byCourier.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell sx={{ ...tCellSx, whiteSpace: 'normal' }}>
                      <Typography sx={strongSx}>{c.carrierName}</Typography>
                      <Typography sx={{ fontFamily: MONO, fontSize: SIZE['4xs'], color: SUBTLE }}>
                        {c.accountCode} · {c.serviceType}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tNumSx}>{int(c.shipments)}</TableCell>
                    <TableCell sx={tNumSx}>{c.delivery}%</TableCell>
                    <TableCell sx={tNumSx}>{c.rto}%</TableCell>
                    <TableCell sx={tNumSx}>{c.inTransit}%</TableCell>
                    <TableCell sx={tNumSx}>{c.firstAttempt}%</TableCell>
                    <TableCell sx={tNumSx}>{c.tat}</TableCell>
                    <TableCell sx={tNumSx}>
                      {c.hasRateCard ? (
                        inr(c.cost)
                      ) : (
                        <Tooltip title='No rate card uploaded for this account, so ProShip cannot price what it carried.'>
                          <Typography sx={{ ...smallSx, color: WARNING }}>no rate card</Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Cut>

        {/* ── Trend and geography ── */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Cut
              title='Is the gap closing?'
              blurb='Share of your volume moving to Direct couriers, week by week.'
            >
              <BarChart
                data={trend.map((t) => ({ week: t.label, Direct: t.directShare }))}
                height={220}
                chartOptions={{
                  xaxis: 'week',
                  data: [{ dataKey: 'Direct', label: 'Direct share (%)', fill: OWN_COLOR }],
                }}
              />
            </Cut>
          </Grid>
          <Grid item xs={12} md={6}>
            <Cut title='Top destination states' blurb='Where a weak lane costs you the most volume.'>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <PairedHead
                    solo={[
                      { label: 'State', minWidth: 120 },
                      { label: 'Direct shipments', minWidth: 90, align: 'right' },
                    ]}
                    pairs={[{ label: 'Delivered', minWidth: 80 }]}
                  />
                  <TableBody>
                    {topStates.map((s) => (
                      <TableRow key={s.state}>
                        <TableCell sx={tCellSx}>{s.state}</TableCell>
                        <TableCell sx={tNumSx}>{int(s.ownShipments)}</TableCell>
                        <TableCell sx={pairCellSx}>{s.ownDelivery}%</TableCell>
                        <TableCell sx={tNumSx}>{s.proshipDelivery}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Cut>
          </Grid>
        </Grid>

        {/* ── Fallback ── */}
        <Cut
          title='Orders that fell back to ProShip'
          blurb='Meant for a Direct courier, carried by ProShip. Nothing looks broken — the parcel arrives — which is exactly why this needs its own number.'
        >
          <Grid container spacing={1.5}>
            {fallback.map((f) => (
              <Grid item xs={6} md={3} key={f.code}>
                <Box sx={statCardSx}>
                  <Typography sx={statLabelSx}>{f.name}</Typography>
                  <Typography sx={{ ...statValueSx, color: f.value > 0 ? WARNING : NAVY }}>
                    {int(f.value)}
                  </Typography>
                  <Typography sx={{ ...statSubSx, fontFamily: MONO, fontSize: 10 }}>{f.code}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 1.5, p: 1.25, bgcolor: LIGHT_BG, borderRadius: RADIUS.md, border: `1px solid ${BORDER}` }}>
            <Typography sx={bodySx}>
              Most fallbacks mean the lane is outside the coverage you uploaded, or the parcel was
              outside the contract limits on the account. Both are fixable in Courier Setup — and
              until they are, that volume stays with ProShip whatever this page says about savings.
            </Typography>
          </Box>
        </Cut>

        <Typography sx={smallSx}>
          Figures on this page are illustrative. Allocation does not yet record which pool carried a
          shipment, so no query can produce a real Direct-versus-ProShip split today.
        </Typography>
      </Box>
    </Box>
  );
}

export default withLayout(DirectPerformance);
